import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Ticket,
  User,
  DollarSign,
  Search,
  RefreshCw,
  FileText,
  ChevronRight,
  ChevronLeft,
  Clock,
  CheckCircle2,
  AlertCircle,
  MapPin,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import agentCustomerEventsService from "../../api/AgentCustomerEvents";
import HeroSlider from "../../components/event_media/HeroSlider";
import AuthenticatedFooter from "../../components/landing/AuthenticatedFooter";
import Images from "../../utils/Images";

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;

function formatCurrency(value, currency = "GHS") {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  const symbol = String(currency || "GHS").toUpperCase() === "USD" ? "$" : "GH₵";
  return `${symbol} ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateString) {
  if (!dateString) return "—";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return String(dateString);
  }
}

function formatDateTime(dateString) {
  if (!dateString) return "—";
  try {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dateString);
  }
}

function getItemDisplay(item) {
  const event = item.event ?? {};
  const clients = item.clients ?? {};
  const ticketType = item.ticket_type;
  const clientName =
    [clients.first_name, clients.last_name].filter(Boolean).join(" ").trim() ||
    item.contact_person_name ||
    item.applicant_name ||
    item.company_name ||
    "—";
  return {
    title: event.event_title ?? "Event",
    customerName: clientName,
    amount: ticketType?.price ?? item.amount ?? item.total,
    currency: ticketType?.currency ?? item.currency ?? "GHS",
    date: event.event_date ?? item.event_date,
    purchasedAt: item.declaration_date ?? item.created_at,
    status: item.payment_status ?? "pending",
    ticketType: typeof ticketType === "object" && ticketType !== null ? ticketType.name : ticketType,
    slug: item.event_slug ?? event.slug,
    id: item.slug ?? item.event_slug,
    eventType: item.event_type,
    venue: event.venue_name,
    city: event.city,
    country: event.country,
    thumbnail: event.thumbnail,
    eventTime: event.event_time,
  };
}

function SkeletonCard() {
  return (
    <div className="overflow-hidden rounded-2xl border-2 border-gray-200 bg-white shadow-lg">
      <div className="p-6">
        <div className="flex gap-4">
          <div className="h-14 w-14 flex-shrink-0 rounded-xl bg-gray-200 animate-pulse" />
          <div className="min-w-0 flex-1 space-y-3">
            <div className="h-5 w-3/4 rounded bg-gray-200 animate-pulse" />
            <div className="h-4 w-1/2 rounded bg-gray-100 animate-pulse" />
            <div className="flex gap-4">
              <div className="h-4 w-24 rounded bg-gray-100 animate-pulse" />
              <div className="h-4 w-20 rounded bg-gray-100 animate-pulse" />
            </div>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <div className="h-8 w-24 rounded-lg bg-gray-100 animate-pulse" />
          <div className="h-8 w-20 rounded-lg bg-gray-100 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

export default function PurchasedEvents() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await agentCustomerEventsService.getAllCustomerEvents();
      const items = response.list ?? [];
      if (response.status) {
        setList(Array.isArray(items) ? items : []);
      } else {
        setError(response.message || "Failed to load purchased events");
        setList([]);
        toast.error(response.message || "Failed to load purchased events");
      }
    } catch (err) {
      setError(err?.message || "An error occurred");
      setList([]);
      toast.error(err?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const filteredList = useMemo(() => {
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase().trim();
    return list.filter((item) => {
      const d = getItemDisplay(item);
      return (
        d.title.toLowerCase().includes(q) ||
        d.customerName.toLowerCase().includes(q) ||
        (d.ticketType && String(d.ticketType).toLowerCase().includes(q)) ||
        (d.status && String(d.status).toLowerCase().includes(q)) ||
        (d.eventType && String(d.eventType).toLowerCase().includes(q)) ||
        (d.venue && d.venue.toLowerCase().includes(q)) ||
        (d.city && d.city.toLowerCase().includes(q))
      );
    });
  }, [list, searchQuery]);

  const totalFiltered = filteredList.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const paginatedList = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredList.slice(start, start + pageSize);
  }, [filteredList, safePage, pageSize]);

  // Reset to page 1 when filters or page size change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, pageSize]);

  const heroImages = [Images.hero_img_web_three, Images.hero_img_web_four].filter(Boolean);
  const heroSlides = heroImages.length ? heroImages : [Images.hero_img_web_one];

  const handleCardClick = (item) => {
    const slug = item.event_slug ?? item.slug;
    if (slug) {
      navigate(`/event-media-sales/event-details/${slug}`);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <HeroSlider
        images={heroSlides}
        showBackButton
        backLink="/event-media-sales"
        backLabel="Back to Event & Media Sales"
      />

      <section className="flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          {/* Page header */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                  Purchased Events
                </h1>
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                  <Ticket size={12} />
                  Your client purchases
                </span>
              </div>
              <p className="mt-1 max-w-xl text-sm text-gray-600">
                All event and conference registrations made by you for your clients.
              </p>
              <div className="mt-2 h-1 w-24 rounded bg-gradient-to-r from-emerald-500 to-teal-500" />
            </div>
            <motion.button
              type="button"
              onClick={() => fetchEvents()}
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-700 shadow-sm transition-colors hover:bg-emerald-50 disabled:opacity-60"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </motion.button>
          </motion.div>

          {/* Search */}
          {list.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="relative mb-6"
            >
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search by event, client name, or ticket type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-xl border-2 border-gray-200 bg-white py-3 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              />
            </motion.div>
          )}

          {/* Content */}
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                {[1, 2, 3, 4, 5].map((i) => (
                  <SkeletonCard key={i} />
                ))}
              </motion.div>
            ) : error ? (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center rounded-2xl border-2 border-amber-200 bg-amber-50/80 py-16 px-6"
              >
                <AlertCircle className="mb-4 h-14 w-14 text-amber-500" />
                <h3 className="mb-2 text-lg font-bold text-amber-900">Unable to load purchases</h3>
                <p className="mb-6 max-w-md text-center text-sm text-amber-800">{error}</p>
                <motion.button
                  type="button"
                  onClick={fetchEvents}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-amber-600"
                >
                  Try again
                </motion.button>
              </motion.div>
            ) : filteredList.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50/80 py-20 px-6"
              >
                <FileText className="mb-4 h-16 w-16 text-gray-400" />
                <h3 className="mb-2 text-xl font-bold text-gray-700">
                  {searchQuery.trim() ? "No matching purchases" : "No purchased events yet"}
                </h3>
                <p className="max-w-md text-center text-sm text-gray-600">
                  {searchQuery.trim()
                    ? "Try a different search term."
                    : "Event and conference registrations you make for clients will appear here."}
                </p>
                {searchQuery.trim() && (
                  <motion.button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="mt-6 text-sm font-semibold text-emerald-600 hover:text-emerald-700"
                  >
                    Clear search
                  </motion.button>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                {paginatedList.map((item, index) => {
                  const d = getItemDisplay(item);
                  const isClickable = !!d.slug;
                  const hasAmount = d.amount != null && d.amount !== "";
                  const eventTypeLabel =
                    d.eventType === "conference"
                      ? "Conference"
                      : d.eventType === "trade_mission"
                      ? "Trade mission"
                      : d.eventType
                      ? String(d.eventType).replace(/_/g, " ")
                      : null;
                  const isPaid =
                    String(d.status || "").toLowerCase() === "paid" ||
                    String(d.status || "").toLowerCase() === "completed";
                  return (
                    <motion.div
                      key={d.id ?? index}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.04 }}
                      onClick={() => isClickable && handleCardClick(item)}
                      className={`overflow-hidden rounded-2xl border-2 bg-white shadow-lg transition-all ${
                        isClickable
                          ? "cursor-pointer border-gray-200 hover:border-emerald-300 hover:shadow-xl"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row">
                        {/* Thumbnail or icon */}
                        <div className="relative h-36 w-full flex-shrink-0 overflow-hidden bg-gray-100 sm:h-auto sm:w-44">
                          {d.thumbnail ? (
                            <img
                              src={d.thumbnail}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600">
                              <Ticket className="h-12 w-12 text-white/90" />
                            </div>
                          )}
                          {eventTypeLabel && (
                            <span className="absolute left-2 top-2 rounded-md bg-black/60 px-2 py-0.5 text-xs font-medium capitalize text-white">
                              {eventTypeLabel}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-1 flex-col p-5 sm:flex-row sm:items-start sm:justify-between sm:p-6">
                          <div className="min-w-0 flex-1">
                            <h3 className="truncate text-lg font-bold text-gray-900">
                              {d.title}
                            </h3>
                            {/* Event type - clear label */}
                            {eventTypeLabel && (
                              <p className="mt-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Event type:{" "}
                                <span className="normal-case text-emerald-600">
                                  {eventTypeLabel}
                                </span>
                              </p>
                            )}
                            {/* Purchased for - clear label */}
                            <div className="mt-2 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                                <User size={18} className="text-emerald-600" />
                              </div>
                              <div className="min-w-0">
                                <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                                  Purchased for
                                </span>
                                <p className="truncate text-sm font-semibold text-gray-900">
                                  {d.customerName}
                                </p>
                              </div>
                            </div>
                            {d.ticketType && (
                              <p className="mt-2 flex items-center gap-1.5 text-sm text-gray-500">
                                <FileText size={14} className="flex-shrink-0 text-gray-400" />
                                <span>
                                  Ticket: <span className="font-medium text-gray-700">{d.ticketType}</span>
                                </span>
                              </p>
                            )}
                            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                              {d.date && (
                                <span className="inline-flex items-center gap-1">
                                  <Calendar size={12} />
                                  {formatDate(d.date)}
                                </span>
                              )}
                              {(d.venue || d.city) && (
                                <span className="inline-flex items-center gap-1">
                                  <MapPin size={12} />
                                  {[d.venue, d.city].filter(Boolean).join(", ")}
                                </span>
                              )}
                              {d.purchasedAt && (
                                <span className="inline-flex items-center gap-1">
                                  <Clock size={12} />
                                  Registered {formatDate(d.purchasedAt)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="mt-4 flex flex-shrink-0 flex-wrap items-center gap-3 sm:mt-0 sm:flex-col sm:items-end">
                            {hasAmount && (
                              <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1.5">
                                <DollarSign size={16} className="text-emerald-600" />
                                <span className="text-base font-bold text-emerald-700">
                                  {formatCurrency(d.amount, d.currency)}
                                </span>
                              </div>
                            )}
                            {d.status && (
                              <span
                                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${
                                  isPaid
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-amber-200 bg-amber-50 text-amber-700"
                                }`}
                              >
                                {isPaid ? (
                                  <CheckCircle2 size={12} />
                                ) : (
                                  <Clock size={12} />
                                )}
                                {String(d.status).replace(/_/g, " ")}
                              </span>
                            )}
                            {isClickable && (
                              <ChevronRight
                                size={20}
                                className="hidden text-gray-400 sm:block"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pagination */}
          {!loading && !error && filteredList.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mt-8 flex flex-col gap-4 border-t border-gray-200 pt-6 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-gray-600">
                  Showing{" "}
                  <span className="font-semibold text-gray-900">
                    {(safePage - 1) * pageSize + 1}–
                    {Math.min(safePage * pageSize, totalFiltered)}
                  </span>{" "}
                  of <span className="font-semibold text-gray-900">{totalFiltered}</span>
                  {searchQuery.trim() && list.length !== filteredList.length && (
                    <span className="text-gray-500"> (filtered from {list.length})</span>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <label htmlFor="page-size" className="text-sm text-gray-500">
                    Per page
                  </label>
                  <select
                    id="page-size"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <motion.button
                  type="button"
                  disabled={safePage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  whileHover={safePage > 1 ? { scale: 1.02 } : {}}
                  whileTap={safePage > 1 ? { scale: 0.98 } : {}}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50 hover:border-gray-400"
                >
                  <ChevronLeft size={18} />
                  Previous
                </motion.button>
                <span className="px-2 text-sm text-gray-600">
                  Page <span className="font-semibold text-gray-900">{safePage}</span> of{" "}
                  <span className="font-semibold text-gray-900">{totalPages}</span>
                </span>
                <motion.button
                  type="button"
                  disabled={safePage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  whileHover={safePage < totalPages ? { scale: 1.02 } : {}}
                  whileTap={safePage < totalPages ? { scale: 0.98 } : {}}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50 hover:border-gray-400"
                >
                  Next
                  <ChevronRight size={18} />
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <AuthenticatedFooter />
    </div>
  );
}
